import { watch } from "vue";
import { useMainStore } from "../composables/store";
import userCenterStore from "../user-center/src/store";
import { getCurrentServer, getStoreInfoDataFromMap } from "../utils/globalData";
import { initializeGoogleAnalytics } from "../utils/googleAnalytics.js";
import {
  createMallEventLogContextFromNuxtStore,
  initializeMallEventLog,
  mallAnalyticsLogEvent,
  mallAnalyticsLogScreenView,
  setMallEventLogContextProvider,
} from "../utils/mallEventLog.js";
import { setPendingPixelPurchaseHeartbeatEnabled } from "../utils/pendingPixelPurchase.js";
import { initializePixelAnalytics } from "../utils/pixelAnalytics.js";

function parseJsonObject(value) {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function getStoreOtherJson(storeInfo = {}) {
  const offiaccountFollowData = parseJsonObject(
    storeInfo?.offiaccountFollowData || storeInfo?.offiaccountFollowUrl,
  );
  const candidates = [
    storeInfo?.offiaccountFollowData?.otherJson,
    offiaccountFollowData?.otherJson,
    storeInfo?.otherJson,
    storeInfo?.storePageInfo?.otherJson,
    storeInfo?.storePageInfo,
  ];
  for (const candidate of candidates) {
    const parsed = parseJsonObject(candidate);
    if (Object.keys(parsed).length) return parsed;
  }
  return {};
}

function getVuexStoreInfo() {
  return userCenterStore.getters?.storeInfo || userCenterStore.state?.app?.storeInfoData || {};
}

export default defineNuxtPlugin((nuxtApp) => {
  const mainStore = useMainStore();

  setMallEventLogContextProvider(() => {
    const context = createMallEventLogContextFromNuxtStore(mainStore, getCurrentServer());
    const vuexStoreInfo = getVuexStoreInfo();
    if (!vuexStoreInfo || typeof vuexStoreInfo !== "object" || !Object.keys(vuexStoreInfo).length) {
      return context;
    }
    const otherJson = getStoreOtherJson(vuexStoreInfo);
    return {
      ...context,
      storeInfo: vuexStoreInfo,
      mallApiUrl: otherJson.mallApiUrl || context.mallApiUrl,
    };
  });

  watch(
    () => ({
      vuexStoreInfo: getVuexStoreInfo(),
      mainStoreInfo: getStoreInfoDataFromMap(mainStore.storeInfoDataMap, getCurrentServer()),
      siteConfigData: mainStore.getSiteInfo()?.configData,
    }),
    ({ vuexStoreInfo, mainStoreInfo, siteConfigData }) => {
      const configCandidates = [
        getStoreOtherJson(vuexStoreInfo),
        getStoreOtherJson(mainStoreInfo),
        siteConfigData,
      ];
      for (const configData of configCandidates) {
        if (initializeGoogleAnalytics(configData)) break;
      }
      let pixelEnabled = false;
      for (const configData of configCandidates) {
        pixelEnabled = initializePixelAnalytics(configData);
        if (pixelEnabled) break;
      }
      setPendingPixelPurchaseHeartbeatEnabled(pixelEnabled);
      initializeMallEventLog();
    },
    { immediate: true },
  );

  mallAnalyticsLogEvent("app_init", {
    init_success: true,
    screenName: window.location.pathname || "/",
    pageCode: "app",
    platform: "web",
  });

  const router = nuxtApp.$router;
  const currentRoute = router?.currentRoute?.value;
  if (currentRoute) {
    mallAnalyticsLogScreenView(
      currentRoute.path || currentRoute.name || window.location.pathname || "unknown_page",
      {
        ...(currentRoute.query || {}),
        path: currentRoute.path,
        fullPath: currentRoute.fullPath,
      },
    );
  }
  if (router?.afterEach) {
    router.afterEach((to, from) => {
      mallAnalyticsLogScreenView(
        to?.path || to?.name || "unknown_page",
        {
          ...(to?.query || {}),
          path: to?.path,
          fullPath: to?.fullPath,
        },
        from?.path || from?.name,
        from?.fullPath,
      );
    });
  }

  nuxtApp.provide("mallEventLog", {
    initialize: initializeMallEventLog,
    logEvent: mallAnalyticsLogEvent,
    logScreenView: mallAnalyticsLogScreenView,
  });
});
